/**
 * ThumbnailPickerSheet -- bottom sheet for selecting a harvest thumbnail photo.
 *
 * M81 migration: removed the legacy URL.createObjectURL objectUrls state and
 * associated useEffect. Each photo is now rendered via PhotoThumbnail which uses
 * usePhotoSrc for on-demand hydration. M83 (ADR-044): the underlying IDB
 * representation is ArrayBuffer; the Blob is reconstructed inside usePhotoSrc.
 */

import { useEffect, useRef, useState } from 'react';
import { X, Check } from 'lucide-react';

import type { PhotoRecord } from '@/types/photo';
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock';
import { PhotoThumbnail } from '@/components/photos/PhotoThumbnail';

// ─── ThumbnailPickerSheet ─────────────────────────────────────────────────────

interface ThumbnailPickerSheetProps {
  isOpen: boolean;
  photos: PhotoRecord[];
  currentThumbnailId?: string;
  onSelect: (photoId: string | null) => void;
  onClose: () => void;
}

/**
 * Bottom sheet for selecting a harvest thumbnail photo.
 * Shows a grid of photo thumbnails; tapping a photo sets it as the thumbnail
 * (or clears it if already selected). The current thumbnail is indicated
 * with a checkmark overlay and highlight border.
 */
export function ThumbnailPickerSheet({
  isOpen,
  photos,
  currentThumbnailId,
  onSelect,
  onClose,
}: ThumbnailPickerSheetProps) {
  // hasMounted gates the translate animation — waits one rAF cycle so
  // translate-y-full renders before we apply translate-y-0.
  const [hasMounted, setHasMounted] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useBodyScrollLock(isOpen);

  useEffect(() => {
    let rafId: number;
    rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(() => {
        setHasMounted(true);
      });
    });
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      // SAFETY: cast is safe — document.activeElement is always HTMLElement or null
      triggerRef.current = document.activeElement as HTMLElement | null;
      dialogRef.current?.focus();
    } else {
      if (triggerRef.current?.isConnected) {
        triggerRef.current.focus();
      } else {
        document.body.focus();
      }
      triggerRef.current = null;
    }
  }, [isOpen]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  function handleSelect(photoId: string) {
    // Toggle: if already thumbnail, clear it
    onSelect(currentThumbnailId === photoId ? null : photoId);
  }

  return (
    <div data-testid="thumbnail-picker-sheet" data-state={isOpen ? 'open' : 'closed'}>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Choose thumbnail"
        className={`fixed inset-x-0 bottom-0 z-50 bg-[var(--color-surface)] border-t border-[var(--color-border)] rounded-t-2xl max-h-[80vh] overflow-y-auto transition-transform duration-300 focus:outline-none ${
          isOpen && hasMounted ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="p-4 pb-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-display-sm text-[var(--color-text-primary)]">
              Choose Thumbnail
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-md text-[var(--color-text-placeholder)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-elevated)] transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Photo grid */}
          {photos.length === 0 ? (
            <p className="text-body-sm text-[var(--color-text-muted)] text-center py-8">
              No photos attached to this harvest.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {photos.map((photo) => {
                const isSelected = photo.id === currentThumbnailId;
                return (
                  <button
                    key={photo.id}
                    type="button"
                    onClick={() => handleSelect(photo.id)}
                    aria-pressed={isSelected}
                    data-testid={`thumbnail-btn-${photo.id}`}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                      isSelected
                        ? 'border-[var(--color-accent)]'
                        : 'border-transparent hover:border-[var(--color-border-subtle)]'
                    }`}
                  >
                    {/* M81: PhotoThumbnail handles hydration via usePhotoSrc */}
                    <PhotoThumbnail
                      photoId={photo.id}
                      alt="Harvest photo"
                      className="w-full h-full object-cover"
                    />
                    {isSelected && (
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <Check size={24} className="text-white" aria-hidden />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
